import React, { useEffect, useState } from 'react';
import { calendarApi } from './api';
import { TODAY_RED, itemWhen } from './calendarToday';
import { noticeView } from './calendarNotices';
import useCalendarNotices from './useCalendarNotices';
import NotesReader from './NotesReader';
import { ReminderRow, TodayItemRow } from './TodayRows';

// =============================================================================
// 5.34.0 the red "Today" panel — since 5.50.0 on the CALENDAR PAGE ONLY (Main and the HealthSherpa page use the Today
// card, TodayCard.jsx). The logged-in user's OWN items for today (Chicago): today's events in their own Personal
// calendar + their tasks due today (GET /api/calendar/today; the server decides). Data + polling: useCalendarNotices.
//   open      -> the red panel; – minimizes, ✕ dismisses for the rest of today (a new item for today brings it back);
//   minimized -> a small red tab with the count; click to reopen.
// 5.50.0: the purple reminder pop-up is gone from this page — a reminder that comes due shows INSIDE this panel, at the
// top and highlighted, with its own Dismiss (and Open, which jumps the calendar to it). A firing reminder shows even
// when the list was minimized / dismissed; its Dismiss is remembered on the server, so no other page alerts again.
// An in-flow right column (docked), so it never covers the calendar toolbar or pane dropdowns.
// 5.43.0: an item with notes gets "⤢ Expand" (NotesReader, full-page); editable when the item is (own calendar).
// =============================================================================

const DARK = '#2b0b0b';

export default function TodayPanel({ onOpenEvent, onOpenTasks, onLayout }) {
  const { today: data, alerts, rest, busy, err, setState, complete, dismissReminder, reload } = useCalendarNotices();
  const [reader, setReader] = useState(null);       // 5.43.0 the item whose notes are open full-page
  const view = noticeView(data, alerts);
  const mode = view.mode === 'card' ? 'open' : view.mode === 'tab' ? 'minimized' : 'none';
  useEffect(() => { if (onLayout) onLayout(mode); }, [mode, onLayout]);

  const openItem = (i) => {
    if (i.kind === 'task') { if (onOpenTasks) onOpenTasks(); return; }
    if (onOpenEvent) onOpenEvent(data.day, i.object_id);
  };
  const openReminder = (r) => {
    if (r.kind === 'task') { if (onOpenTasks) onOpenTasks(); return; }
    const a = alerts.find((x) => x.reminder.key === r.key);
    const day = (a && a.item && data && data.day) || String(r.start || '').slice(0, 10);
    if (onOpenEvent && day) onOpenEvent(day, r.object_id);
  };

  if (view.mode === 'none') return null;

  if (view.mode === 'tab') {
    return (
      <button type="button" data-testid="today-tab" onClick={() => setState('open')} title={`Today — ${data.count} item${data.count === 1 ? '' : 's'}. Click to open.`}
        style={{ alignSelf: 'start', margin: '12px 0 0',
          background: TODAY_RED, color: '#fff', border: 'none', borderRadius: '8px 0 0 8px', padding: '10px 6px', cursor: 'pointer',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, boxShadow: '0 4px 14px rgba(0,0,0,0.35)', fontWeight: 700 }}>
        <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontSize: 12, letterSpacing: '0.08em', color: '#fff' }}>TODAY</span>
        <span data-testid="today-tab-count" style={{ background: '#fff', color: TODAY_RED, borderRadius: 10, minWidth: 20, padding: '1px 5px', fontSize: 12, textAlign: 'center' }}>{data.count}</span>
      </button>
    );
  }

  const readerEl = reader && data && (
    <NotesReader title={reader.title} notes={reader.notes} canEdit={!!reader.can_edit && !!reader.etag}
      when={`${itemWhen(reader, data.day)} · Today, ${data.label} (Central)`}
      onSave={async (text) => { await calendarApi.update(reader.object_id, reader.etag, { notes: text }); reload(); return true; }}
      onClose={() => setReader(null)} />
  );
  const hasToday = !!(data && data.enabled && data.count > 0);

  return (
    <div data-testid="today-panel" role="complementary" aria-label="Today"
      style={{ width: 290, margin: 12, marginLeft: 0, maxHeight: 'calc(100% - 24px)', alignSelf: 'start',
        display: 'flex', flexDirection: 'column', background: DARK, border: `2px solid ${TODAY_RED}`, borderRadius: 10,
        boxShadow: '0 8px 28px rgba(0,0,0,0.45)', color: '#fff', overflow: 'hidden', boxSizing: 'border-box' }}>
      <div style={{ background: TODAY_RED, padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 14, color: '#fff' }} data-testid="today-title">Today{data ? ` — ${data.label}` : ''}</div>
        {hasToday && <span data-testid="today-count" style={{ background: '#fff', color: TODAY_RED, borderRadius: 10, padding: '1px 7px', fontSize: 12, fontWeight: 700 }}>{data.count}</span>}
        {view.canHide && (
          <>
            <button type="button" onClick={() => setState('minimized')} data-testid="today-minimize" title="Minimize to a tab"
              style={{ background: 'rgba(0,0,0,0.25)', color: '#fff', border: 'none', borderRadius: 5, width: 24, height: 22, cursor: 'pointer', fontWeight: 700 }}>–</button>
            <button type="button" onClick={() => setState('dismissed')} data-testid="today-dismiss" title="Hide for the rest of today (it comes back if something new is added for today)"
              style={{ background: 'rgba(0,0,0,0.25)', color: '#fff', border: 'none', borderRadius: 5, width: 24, height: 22, cursor: 'pointer', fontWeight: 700 }}>✕</button>
          </>
        )}
      </div>
      {err && <div style={{ background: '#7f1d1d', color: '#fecaca', fontSize: 12, padding: '5px 10px' }}>{err}</div>}
      <div style={{ overflowY: 'auto', padding: alerts.length ? 0 : '4px 0' }}>
        {alerts.map(({ reminder }) => (
          <ReminderRow key={reminder.key} r={reminder} onDismiss={dismissReminder} onOpenCalendar={openReminder} />
        ))}
        {view.showReopen && (
          <button type="button" onClick={() => setState('open')} data-testid="today-reopen"
            style={{ display: 'block', width: '100%', padding: '7px 10px', background: 'none', border: 'none', color: '#fecaca', fontSize: 12, cursor: 'pointer', textAlign: 'left' }}>
            Show today's {data.count} item{data.count === 1 ? '' : 's'} ▾</button>
        )}
        {view.showToday && rest.map((i) => (
          <TodayItemRow key={i.id} i={i} day={data.day} accent={TODAY_RED} busy={busy}
            onComplete={complete} onOpen={openItem} onExpand={setReader} />
        ))}
      </div>
      {readerEl}
    </div>
  );
}
